import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Alert, Button, Form, Input, Select,
} from 'antd';

const { TextArea } = Input;
const { Option } = Select;
const warnedComponents = new Set();

const FIELD_COMPONENTS = new Set(['select', 'checkbox', 'text', 'textarea']);

const withoutField = (value, fieldName) => {
  const next = { ...value };
  delete next[fieldName];
  return next;
};

const fieldExtra = (field) => {
  if (field.component === 'checkbox') {
    return 'Inherit keeps today\'s behavior: the box is left unchecked in eCW.';
  }
  if (field.internal_name === 'notes') {
    return 'Sent to eCW as \'Auto Uploaded by Medsender: <your text>\'. Leave empty to send the attribution only, or reset to send no description.';
  }
  return field.help_text;
};

const RuleDocumentDefaultsFields = ({
  fields, value, onChange, credentialId, providerError,
}) => {
  const [optionsBySource, setOptionsBySource] = useState({});
  const [loadingSources, setLoadingSources] = useState({});
  const [failedSources, setFailedSources] = useState({});

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const sources = [...new Set(fields.flatMap((field) => (field.options_source ? [field.options_source] : [])))];
    if (!credentialId || sources.length === 0) return undefined;

    const loadOptions = async () => {
      setLoadingSources(Object.fromEntries(sources.map((source) => [source, true])));
      setFailedSources((current) => ({
        ...current,
        ...Object.fromEntries(sources.map((source) => [source, false])),
      }));
      await Promise.all(sources.map(async (source) => {
        try {
          const response = await axios.get('/api/v1/emr_options', {
            params: { emr_integration_credential_id: credentialId, source },
            signal: controller.signal,
            timeout: 10000,
          });
          if (!cancelled) {
            setOptionsBySource((current) => ({ ...current, [source]: response.data.options || [] }));
          }
        } catch {
          if (!cancelled && !controller.signal.aborted) setFailedSources((current) => ({ ...current, [source]: true }));
        } finally {
          if (!cancelled) setLoadingSources((current) => ({ ...current, [source]: false }));
        }
      }));
    };

    loadOptions();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [credentialId, fields]);

  const updateField = (fieldName, nextValue) => {
    const next = nextValue === undefined ? withoutField(value, fieldName) : { ...value, [fieldName]: nextValue };
    onChange(next);
  };

  const renderInput = (field) => {
    const name = field.internal_name;
    const fieldValue = value[name];
    if (!FIELD_COMPONENTS.has(field.component)) {
      if (!warnedComponents.has(field.component)) {
        warnedComponents.add(field.component);
        console.warn(`Unsupported auto-upload document defaults component: ${field.component}`);
      }
      return null;
    }

    if (field.component === 'checkbox') {
      return (
        <Select
          aria-label={field.label}
          value={fieldValue}
          onChange={(nextValue) => updateField(name, nextValue)}
        >
          <Option value={undefined}>Inherit</Option>
          <Option value>Yes</Option>
          <Option value={false}>No</Option>
        </Select>
      );
    }

    if (field.component === 'select') {
      const source = field.options_source;
      const options = optionsBySource[source] || [];
      const loading = loadingSources[source];
      const failed = failedSources[source];
      if (failed) return <Alert type="error" showIcon message={`Could not load ${field.label.toLowerCase()} options. Try again later.`} />;
      if (!loading && source && options.length === 0) {
        return <Alert type="info" showIcon message="No active providers. Add them under Settings → Providers." />;
      }
      return (
        <Select
          aria-label={field.label}
          allowClear
          showSearch
          optionFilterProp="children"
          value={fieldValue}
          loading={loading}
          disabled={loading}
          placeholder={loading ? 'Loading providers...' : `Select ${field.label.toLowerCase()}`}
          onChange={(nextValue) => updateField(name, nextValue)}
        >
          {options.map((option) => <Option key={option.value} value={option.value}>{option.label}</Option>)}
        </Select>
      );
    }

    if (field.component === 'textarea') {
      return (
        <>
          <TextArea
            aria-label={field.label}
            maxLength={500}
            rows={3}
            value={fieldValue || ''}
            onChange={(event) => updateField(name, event.target.value)}
          />
          <div style={{ color: '#888', fontSize: 12, textAlign: 'right' }}>
            {`${(fieldValue || '').length}/500`}
          </div>
        </>
      );
    }

    return (
      <Input
        aria-label={field.label}
        value={fieldValue || ''}
        onChange={(event) => updateField(name, event.target.value)}
      />
    );
  };

  return (
    <>
      {providerError && <Alert style={{ marginBottom: 12 }} type="error" showIcon message={providerError} />}
      {fields.map((field) => {
        const input = renderInput(field);
        if (!input) return null;
        return (
          <Form.Item
            key={field.internal_name}
            label={field.label}
            extra={fieldExtra(field)}
          >
            {input}
            {Object.prototype.hasOwnProperty.call(value, field.internal_name) && (
              <Button type="link" size="small" onClick={() => updateField(field.internal_name, undefined)}>
                Reset to inherit
              </Button>
            )}
          </Form.Item>
        );
      })}
    </>
  );
};

RuleDocumentDefaultsFields.propTypes = {
  fields: PropTypes.array.isRequired,
  value: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  credentialId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  providerError: PropTypes.string,
};

RuleDocumentDefaultsFields.defaultProps = {
  credentialId: null,
  providerError: null,
};

export default RuleDocumentDefaultsFields;
